import React, { useRef, useEffect } from 'react';
import { GetSoDto, handleErrorMessage, showMessage, useCreateSoWithCaptcha, useGeneralSoContext, useSessionStorage, INSURANCE_NEEDS_KEY, PROPERTY_INFO_KEY, SoPropertyInsuranceNeedsInput, SoPropertyInfoInput, useLoader } from 'src/common';
import { CustomerForm, CustomerFormData } from 'src/components/forms/customer.form';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import { useGetSo } from 'src/common/api/apollo/queries/useGetSo.query';
import { CaptchaChallenge, ContainerComponent, Flex, FormDisclaimer, FormRef, LoaderSquare, NavigationButtons, StepHeader } from 'src/components';
import { Helmet } from 'react-helmet-async';

export const CustomerFunnelPage: React.FC = () => {
  const formRef = useRef<FormRef>(null);
  const { so, refetch, loading } = useGetSo();
  const { soId, setSoId, soInitialData, setSoInitialData } = useGeneralSoContext();
  // First createSo of the funnel flow: a creation, so property-api may answer
  // CAPTCHA_REQUIRED past the free tier. The hook renders the widget and replays the
  // identical payload with the token; a resubmit with the returned soId is an edit (free).
  const { submit: createSo, captcha } = useCreateSoWithCaptcha({ page: 'property-funnel-customer' });
  const { startLoader, stopLoader } = useLoader();
  const { getValue: getInsuranceNeeds } = useSessionStorage<SoPropertyInsuranceNeedsInput>(INSURANCE_NEEDS_KEY);
  const { getValue: getPropertyInfo } = useSessionStorage<SoPropertyInfoInput>(PROPERTY_INFO_KEY);

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  const handleBack = () => {
    NavigationService.navigate(AppRoutes.FUNNEL.PROPERTY_INFO);
  };

  const handleFormSubmit = async () => {
    const validated = formRef.current?.validateForm?.();
    const values = formRef.current?.getValues?.();
    if(!values || !validated) return;
    startLoader();
    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phoneNumber
    };

    const insuranceNeeds = getInsuranceNeeds() ?? undefined;
    const propertyInfo = getPropertyInfo() ?? undefined;

    setSoInitialData({ ...soInitialData });

    createSo(
      {
        input: {
          soId,
          data,
          productTypeSpecific: {
            insuranceNeeds,
            propertyInfo,
          },
        },
      },
      {
        onSuccess: ({ data: result }) => {
          if (result?.createSo) setSoId(result.createSo);
          NavigationService.navigate(AppRoutes.FUNNEL.CONTACT);
        },
        onError: (error) => {
          // RATE_LIMITED / IP_BLOCKED are already toasted by rateLimitErrorLink.
          const code = error.graphQLErrors?.[0]?.extensions?.code;
          if (code === 'RATE_LIMITED' || code === 'IP_BLOCKED') return;
          showMessage({
            type: 'error',
            message: handleErrorMessage(error),
            duration: 5000,
            fixed: true
          });
        },
        onSettled: stopLoader,
      },
    );
  };

  const modelToForm = (so?: GetSoDto): CustomerFormData => ({
    firstName: so?.data?.firstName || '',
    lastName: so?.data?.lastName || '',
    email: so?.data?.email || '',
    phoneNumber: so?.data?.phone || '',
  });

  const formData = modelToForm(so || undefined);

  if (loading) return (
    <ContainerComponent>
      <Flex align="center" justify="center" style={{ height: '40vh' }}>
        <LoaderSquare/>
      </Flex>
    </ContainerComponent>
  );

  return (
    <ContainerComponent>
      <Helmet>
        <title>Tvoji podaci | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader/>
      <CustomerForm
        ref={formRef}
        data={formData}
        onSubmit={handleFormSubmit}
      />

      {/* Appears only after the API asked for a captcha; solving it resubmits, so "next" waits. */}
      <CaptchaChallenge {...captcha} />
      <NavigationButtons
        handleBack={handleBack}
        handleNext={handleFormSubmit}
        nextDisabled={captcha.required}
      />
      <FormDisclaimer/>
    </ContainerComponent>
  );
};

export default CustomerFunnelPage;
